import React, { createContext, useContext, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import es from "@/i18n/es";
import en from "@/i18n/en";
import fr from "@/i18n/fr";
import it from "@/i18n/it";
import Layout from "@/components/Layout";
import { aplicarSEO } from "@/components/SEO";
import Home from "@/pages/Home";
import Habitaciones from "@/pages/Habitaciones";
import Gastronomia from "@/pages/Gastronomia";
import Servicios from "@/pages/Servicios";
import Eventos from "@/pages/Eventos";
import EventoTipo from "@/pages/EventoTipo";
import Espacio from "@/pages/Espacio";
import QueHacer from "@/pages/QueHacer";
import Actividad from "@/pages/Actividad";
import Fotos from "@/pages/Fotos";
import GaleriaCategoria from "@/pages/GaleriaCategoria";
import Contacto from "@/pages/Contacto";
import QuienesSomos from "@/pages/QuienesSomos";
import Terminos from "@/pages/Terminos";
import Cookies from "@/pages/Cookies";
import Sustentabilidad from "@/pages/Sustentabilidad";
import Comunidad from "@/pages/Comunidad";
import Faq from "@/pages/Faq";
import InfoUtil from "@/pages/InfoUtil";
import Admin from "@/pages/Admin";

export const LANGS = ["es", "en", "fr", "it"];
const dict = { es, en, fr, it };

const LangCtx = createContext(null);
export const useLang = () => useContext(LangCtx);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-sand p-8">
          <div className="max-w-md text-center">
            <p className="font-serif text-3xl text-ink">La Carla Posada Boutique</p>
            <p className="mt-3 text-sm text-ink-soft">Se produjo un error. Recargá la página para continuar.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteRoutes() {
  const { lang } = useParams();
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="habitaciones" element={<Habitaciones />} />
      <Route path="gastronomia" element={<Gastronomia />} />
      <Route path="servicios" element={<Servicios />} />
      <Route path="eventos" element={<Eventos />} />
      <Route path="eventos/:tipo" element={<EventoTipo />} />
      <Route path="espacios/:slug" element={<Espacio />} />
      <Route path="que-hacer" element={<QueHacer />} />
      <Route path="que-hacer/:slug" element={<Actividad />} />
      <Route path="fotos" element={<Fotos />} />
      <Route path="fotos/:cat" element={<GaleriaCategoria />} />
      <Route path="contacto" element={<Contacto />} />
      <Route path="quienes-somos" element={<QuienesSomos />} />
      <Route path="terminos" element={<Terminos />} />
      <Route path="cookies" element={<Cookies />} />
      <Route path="sustentabilidad" element={<Sustentabilidad />} />
      <Route path="comunidad" element={<Comunidad />} />
      <Route path="preguntas-frecuentes" element={<Faq />} />
      <Route path="informacion-util" element={<InfoUtil />} />
      <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
    </Routes>
  );
}

function LangRoot() {
  const { lang } = useParams();
  const location = useLocation();
  const valid = LANGS.includes(lang);
  const ctx = useMemo(() => {
    const l = LANGS.includes(lang) ? lang : "es";
    return {
      lang: l,
      t: dict[l],
      L: (path) => `/${l}${path ? "/" + path : ""}`,
      cambiarIdioma: (nuevo) => {
        const rest = location.pathname.replace(`/${lang}`, "");
        return `/${nuevo}${rest || ""}`;
      },
    };
  }, [lang, location.pathname]);

  useEffect(() => {
    if (valid) document.documentElement.lang = lang;
  }, [lang, valid]);

  useEffect(() => {
    if (!valid) return;
    const ruta = location.pathname.replace(`/${lang}`, "").replace(/^\/+|\/+$/g, "");
    aplicarSEO(ruta, dict[lang]);
  }, [location.pathname, lang, valid]);

  if (!valid) return <Navigate to={`/es${location.pathname}`} replace />;
  return (
    <LangCtx.Provider value={ctx}>
      <Layout>
        <SiteRoutes />
      </Layout>
    </LangCtx.Provider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/" element={<Navigate to="/es" replace />} />
        <Route path="/:lang/*" element={<LangRoot />} />
        <Route path="*" element={<Navigate to="/es" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}